/**
 * useDrawer composable — controls the right filter-drawer's open state.
 *
 * Module-scoped refs ensure all consumers share the same drawer state
 * regardless of where in the tree they invoke the composable. The drawer
 * is push-style (not overlay) per D-012.
 *
 * Sider leverer indhold til draweren via <FilterDrawerContent> (teleport til
 * #filter-drawer-body) — den registrerer sig her, så draweren ved om den
 * aktive side overhovedet HAR filtre (hasContent). Når den sidste
 * indholdsleverandør unmountes (navigation til en side uden filtre), lukkes
 * draweren automatisk — nextTick-checket lader et rute-skifte mellem to
 * filter-sider (unmount → mount i samme patch) beholde draweren åben.
 */

import { computed, nextTick, ref } from 'vue';

const isOpen = ref(false);
const contentCount = ref(0);

// Sideindstillings-draweren (12-08-2026) deler modul-scope med filter-
// draweren, så de to kan være GENSIDIGT udelukkende (kun én åben ad gangen —
// to samtidige push-drawers ville æde hele indholdsbredden).
const settingsOpen = ref(false);

// Handlings-draweren ([D-334], 28-09-2026): tredje push-drawer i skallen, som
// en side åbner fra sin handlingsknap (fx "Tilpas varenumre" på Lagervarer).
// Samme modul-scope, så alle 3 er gensidigt udelukkende. Titlen sættes af
// sidens ActionDrawerContent, fordi den følger sidens tilstand.
const actionOpen = ref(false);
const actionContentCount = ref(0);
const actionTitle = ref('');

export function useDrawer() {
  function open(): void {
    isOpen.value = true;
    settingsOpen.value = false;
    actionOpen.value = false;
  }

  function close(): void {
    isOpen.value = false;
  }

  function toggle(): void {
    if (isOpen.value) close();
    else open();
  }

  /** Registrér at den aktive side leverer drawer-indhold. Returnerer
   *  unregister-funktionen (kaldes af FilterDrawerContent ved unmount). */
  function registerContent(): () => void {
    contentCount.value += 1;
    return () => {
      contentCount.value -= 1;
      if (contentCount.value === 0) {
        void nextTick(() => {
          if (contentCount.value === 0) isOpen.value = false;
        });
      }
    };
  }

  const hasContent = computed(() => contentCount.value > 0);

  return { isOpen, hasContent, open, close, toggle, registerContent };
}

/** Sideindstillings-drawerens delte tilstand (tandhjulet i headeren). */
export function useSettingsDrawer() {
  function open(): void {
    settingsOpen.value = true;
    isOpen.value = false;
    actionOpen.value = false;
  }

  function close(): void {
    settingsOpen.value = false;
  }

  function toggle(): void {
    if (settingsOpen.value) close();
    else open();
  }

  return { isOpen: settingsOpen, open, close, toggle };
}

/**
 * Handlings-drawerens delte tilstand ([D-334]). Siden leverer indholdet via
 * <ActionDrawerContent> og åbner draweren fra sin egen knap i headeren.
 * Forsvinder indholdet (siden forlades), lukker draweren, som filter-draweren.
 */
export function useActionDrawer() {
  function open(): void {
    actionOpen.value = true;
    isOpen.value = false;
    settingsOpen.value = false;
  }

  function close(): void {
    actionOpen.value = false;
  }

  function toggle(): void {
    if (actionOpen.value) close();
    else open();
  }

  function registerContent(): () => void {
    actionContentCount.value += 1;
    return () => {
      actionContentCount.value -= 1;
      if (actionContentCount.value === 0) {
        void nextTick(() => {
          if (actionContentCount.value === 0) actionOpen.value = false;
        });
      }
    };
  }

  const hasContent = computed(() => actionContentCount.value > 0);

  return { isOpen: actionOpen, title: actionTitle, hasContent, open, close, toggle, registerContent };
}
